import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { useTranslation } from "react-i18next";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

interface Feature {
  id: string;
  key: string;
  name: string;
  description?: string | null;
}

interface ProductHoverCardProps {
  productId: string;
  children: React.ReactNode;
}

export function ProductHoverCard({ productId, children }: ProductHoverCardProps) {
  const { t } = useTranslation();

  const { data: product, isLoading, error } = trpc.activation.getProduct.useQuery(
    { id: productId },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes cache
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection time
      retry: 1,
      enabled: !!productId
    }
  );

  const renderFeatureList = () => {
    if (!product) {
      return <p className="text-sm text-muted-foreground">{t("common.noFeaturesAvailable")}</p>;
    }

    const customFeatures = product.customFeatures;

    // 检查是否为空或未定义
    if (!customFeatures ||
        (typeof customFeatures === 'object' && Object.keys(customFeatures).length === 0) ||
        !product.features?.length) {
      // 如果既没有customFeatures也没有features，则显示N/A
      if (!product.features?.length) {
        return <p className="text-sm text-muted-foreground">{t("common.na")}</p>;
      }
    }

    return (
      <div className="space-y-2">
        {/* 标准特性 */}
        {product.features?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {product.features.map((feature) => (
              <Badge
                key={feature.id}
                variant="default"
                className="bg-primary/90 hover:bg-primary text-primary-foreground"
              >
                {feature.name}
              </Badge>
            ))}
          </div>
        )}

        {/* 自定义特性 - 数组格式 */}
        {Array.isArray(customFeatures) && customFeatures.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {customFeatures.map((feature) => {
              const key = feature.keyName;
              const value = feature.value;
              const uniqueKey = feature.id || `feature-${key}-${Math.random().toString(36).substring(2, 9)}`;

              // 布尔值特性
              if (feature.valueType === "boolean" || typeof value === "boolean") {
                const boolValue = Boolean(value);
                return (
                  <Badge
                    key={uniqueKey}
                    variant={boolValue ? "default" : "outline"}
                    className={boolValue ? "bg-primary/90 hover:bg-primary text-primary-foreground" : "text-muted-foreground border-muted-foreground/40 hover:bg-accent"}
                  >
                    {key}
                  </Badge>
                );
              }

              // 数字特性
              if (feature.valueType === "number" || typeof value === "number") {
                return (
                  <Badge
                    key={uniqueKey}
                    variant="secondary"
                    className="bg-secondary/20 text-secondary-foreground hover:bg-secondary/30"
                  >
                    <span className="font-medium mr-1">{key}:</span> {value}
                  </Badge>
                );
              }

              // 字符串特性
              return (
                <Badge
                  key={uniqueKey}
                  variant="secondary"
                  className="bg-muted hover:bg-muted/80"
                >
                  <span className="font-medium mr-1">{key}:</span> {String(value)}
                </Badge>
              );
            })}
          </div>
        )}

        {/* 自定义特性 - 对象格式 */}
        {!Array.isArray(customFeatures) && customFeatures && typeof customFeatures === 'object' && Object.keys(customFeatures).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(customFeatures).map(([key, value]) => {
              const uniqueKey = `feature-obj-${key}-${Math.random().toString(36).substring(2, 9)}`;

              // 布尔值特性
              if (typeof value === "boolean") {
                return (
                  <Badge
                    key={uniqueKey}
                    variant={value ? "default" : "outline"}
                    className={value ? "bg-primary/90 hover:bg-primary text-primary-foreground" : "text-muted-foreground border-muted-foreground/40 hover:bg-accent"}
                  >
                    {key}
                  </Badge>
                );
              }

              // 数字特性
              if (typeof value === "number") {
                return (
                  <Badge
                    key={uniqueKey}
                    variant="secondary"
                    className="bg-secondary/20 text-secondary-foreground hover:bg-secondary/30"
                  >
                    <span className="font-medium mr-1">{key}:</span> {value}
                  </Badge>
                );
              }

              // 字符串特性
              return (
                <Badge
                  key={uniqueKey}
                  variant="secondary"
                  className="bg-muted hover:bg-muted/80"
                >
                  <span className="font-medium mr-1">{key}:</span> {String(value)}
                </Badge>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <div className="cursor-help">{children}</div>
      </HoverCardTrigger>
      <HoverCardContent className="w-80">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <div className="mt-4 space-y-2">
              <Skeleton className="h-5 w-full" />
              <div className="flex gap-2">
                <Skeleton className="h-6 w-20 rounded-full" />
                <Skeleton className="h-6 w-24 rounded-full" />
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="text-destructive">
            {t("common.errorLoadingProduct")}
          </div>
        ) : product ? (
          <>
            <CardHeader className="p-3 pb-1">
              <CardTitle className="text-base">{product.name}</CardTitle>
              {product.description && (
                <CardDescription className="text-xs">{product.description}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="p-3 pt-2 space-y-3">
              {(product.monthlyPrice || product.yearlyPrice) && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">
                    {t("admin.activation.products.pricing")}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.monthlyPrice !== null && (
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-1 text-xs bg-primary/10">
                          {t("admin.activation.products.monthly")}
                        </Badge>
                        <span className="text-xs font-medium">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                          }).format(Number(product.monthlyPrice))}
                        </span>
                      </div>
                    )}
                    {product.yearlyPrice !== null && (
                      <div className="flex items-center">
                        <Badge variant="outline" className="mr-1 text-xs bg-secondary/10">
                          {t("admin.activation.products.yearly")}
                        </Badge>
                        <span className="text-xs font-medium">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                          }).format(Number(product.yearlyPrice))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                  {t("admin.activation.products.features")}:
                </p>
                {renderFeatureList()}
              </div>
            </CardContent>
          </>
        ) : (
          <div className="text-muted-foreground p-3">
            {t("common.productNotFound")}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}